import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import logoExtended from "../../../../assets/images/paisley-logo-extended.png";
import { useDispatch, useSelector } from "react-redux";
import useQuery from "../../../../hooks/useQuery";
import { loadPayment, savePayment } from "../../../../redux/actions/signatory";
import State, { StateStatus } from "../../../../../domain/entities/State";
import { useNavigate } from "react-router-dom";

type FormData = {
  payment: string;
};

const PaymentVariable: React.FC = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      payment: "",
    },
  });
  const query = useQuery();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const paymentId = query.get("payId");
  const [showForm, setShowForm] = useState<boolean>(false);
  const payment = useSelector((state: State) => state.signatory.payment);
  const paymentSaveStatus = useSelector(
    (state: State) => state.signatory.paymentSaveStatus
  );

  const redirectToStripe = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND}/api/payment/${paymentId}`;
  };

  useEffect(() => {
    if (paymentId) {
      dispatch(loadPayment(paymentId));
    } else {
      navigate("/payment-cancelled");
    }
  }, []);

  useEffect(() => {
    if (payment && payment.amount) {
      redirectToStripe();
    } else if (payment && !payment.amount) {
      setShowForm(true);
    }
  }, [payment]);

  useEffect(() => {
    if (paymentSaveStatus === StateStatus.COMPLETE) {
      redirectToStripe();
    }
  }, [paymentSaveStatus]);

  const onSubmit = (data: FormData) => {
    const parsed = parseFloat(data.payment);
    const valueInCents = Math.round(parsed * 100);
    if (payment) {
      const updatedPayment = {
        ...payment,
        amount: valueInCents,
      };

      dispatch(savePayment(updatedPayment));
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      bgcolor="#f5f5f5"
    >
      {showForm ? (
        <Card sx={{ minWidth: 300, textAlign: "center", p: 4 }}>
          <CardContent>
            <Box display="flex" justifyContent="center" mb={3}>
              <img
                src={logoExtended}
                alt="Paisley Logo"
                style={{ maxWidth: "200px", height: "auto" }}
              />
            </Box>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} mt={2}>
              <Controller
                name="payment"
                control={control}
                rules={{
                  required: "Amount is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Enter a valid amount like 5.99",
                  },
                  validate: (value) =>
                    parseFloat(value) > 0 || "Amount must be greater than 0",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Amount (USD)"
                    placeholder="e.g. 5.99"
                    inputMode="decimal"
                    inputProps={{ pattern: "^[0-9]+(\\.[0-9]{1,2})?$" }}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const parsed = parseFloat(raw);
                      if (!isNaN(parsed)) {
                        field.onChange(parsed.toFixed(2));
                      }
                    }}
                    error={!!errors.payment}
                    helperText={
                      typeof errors.payment?.message === "string"
                        ? errors.payment.message
                        : ""
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Pay
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="h6" fontWeight="bold">
          <CircularProgress />
        </Typography>
      )}
    </Box>
  );
};

export default PaymentVariable;
